import React from 'react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode
}

const Toolbar = React.forwardRef<HTMLDivElement, ToolbarProps>(
	({ className, children, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn('flex items-center gap-1 rounded-full border bg-white p-1 shadow-lg', className)}
				{...props}
			>
				{children}
			</div>
		)
	}
)
Toolbar.displayName = 'Toolbar'

interface ToolbarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode
}

const ToolbarGroup = React.forwardRef<HTMLDivElement, ToolbarGroupProps>(
	({ className, children, ...props }, ref) => {
		return (
			<div ref={ref} className={cn('flex items-center gap-1', className)} {...props}>
				{children}
			</div>
		)
	}
)
ToolbarGroup.displayName = 'ToolbarGroup'

interface ToolbarSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {}

const ToolbarSeparator = React.forwardRef<HTMLDivElement, ToolbarSeparatorProps>(
	({ className, ...props }, ref) => {
		return <div ref={ref} className={cn('mx-1 h-6 w-px bg-border', className)} {...props} />
	}
)
ToolbarSeparator.displayName = 'ToolbarSeparator'

interface ToolbarButtonProps
	extends React.ComponentProps<typeof Button>,
		React.ComponentProps<'button'> {
	tooltip?: string
	children: React.ReactNode
	variant?: 'default' | 'outline' | 'ghost' | 'secondary' | 'destructive' | 'link'
	size?: 'default' | 'sm' | 'lg' | 'icon' | 'icon-sm' | 'icon-lg' | 'icon-xs'
}

const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
	({ tooltip, children, className, variant = 'ghost', size, ...props }, ref) => {
		// Determine if it's an icon-only button (no text children, only React elements)
		const isIconOnly = React.useMemo(
			() =>
				React.Children.toArray(children).every(
					(child) => React.isValidElement(child) && typeof child.type !== 'string'
				),
			[children]
		)

		const buttonSize = size || (isIconOnly ? 'icon' : 'sm')

		const button = (
			<Button
				ref={ref}
				variant={variant}
				size={buttonSize}
				className={cn(isIconOnly ? 'h-8 w-8' : '', className)}
				{...props}
			>
				{children}
			</Button>
		)

		if (tooltip) {
			return (
				<Tooltip>
					<TooltipTrigger asChild>{button}</TooltipTrigger>
					<TooltipContent>{tooltip}</TooltipContent>
				</Tooltip>
			)
		}

		return button
	}
)
ToolbarButton.displayName = 'ToolbarButton'

export { Toolbar, ToolbarButton, ToolbarGroup, ToolbarSeparator }
